/**
 * Utility functions for handling dates, especially Firebase Timestamps
 */

/**
 * Safely converts various date formats to a JavaScript Date object
 * Handles Firebase Timestamps, ISO strings, and JavaScript Date objects
 */
export function toSafeDate(dateValue: any): Date | null {
  if (!dateValue) {
    return null;
  }

  // Handle Firebase Timestamp with seconds property
  if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
    return new Date(dateValue.seconds * 1000);
  }

  // Handle Firebase Timestamp with toDate method
  if (dateValue && typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
    try {
      return dateValue.toDate();
    } catch (error) {
      console.warn('Failed to convert Firebase timestamp:', error);
    }
  }

  // Handle Firebase Timestamp with _seconds property (alternative format)
  if (dateValue && typeof dateValue === 'object' && '_seconds' in dateValue) {
    return new Date(dateValue._seconds * 1000);
  }

  // Handle regular JavaScript Date objects
  if (dateValue instanceof Date) {
    return isValidDate(dateValue) ? dateValue : null;
  }

  // Handle ISO date strings
  if (typeof dateValue === 'string') {
    const parsedDate = new Date(dateValue);
    return isValidDate(parsedDate) ? parsedDate : null;
  }

  // Handle numeric timestamps (milliseconds)
  if (typeof dateValue === 'number') {
    const date = new Date(dateValue);
    return isValidDate(date) ? date : null;
  }

  console.warn('Unrecognized date format:', dateValue);
  return null;
}

/**
 * Checks if a Date object is valid
 */
function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Formats a date for display, with fallback for invalid dates
 */
export function formatDate(dateValue: any, options?: Intl.DateTimeFormatOptions): string {
  const date = toSafeDate(dateValue);
  
  if (!date) {
    return 'N/A';
  }

  try {
    return date.toLocaleDateString(undefined, options);
  } catch (error) {
    console.warn('Failed to format date:', error);
    return 'Invalid Date';
  }
}

/**
 * Formats a date and time for display
 */
export function formatDateTime(dateValue: any, options?: Intl.DateTimeFormatOptions): string {
  const date = toSafeDate(dateValue);
  
  if (!date) {
    return 'N/A';
  }

  try {
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    return date.toLocaleString(undefined, defaultOptions);
  } catch (error) {
    console.warn('Failed to format datetime:', error);
    return 'Invalid Date';
  }
}

/**
 * Returns a relative time string (e.g., "2 days ago", "just now")
 */
export function formatRelativeTime(dateValue: any): string {
  const date = toSafeDate(dateValue);
  
  if (!date) {
    return 'N/A';
  }

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
  } else {
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
  }
}

/**
 * Debug function to log the structure of a date value
 */
export function debugDate(dateValue: any, label?: string): void {
  if (process.env.NODE_ENV === 'development') {
    console.log(`Date Debug${label ? ` (${label})` : ''}:`, {
      value: dateValue,
      type: typeof dateValue,
      isDate: dateValue instanceof Date,
      hasSeconds: dateValue && typeof dateValue === 'object' && 'seconds' in dateValue,
      hasToDate: dateValue && typeof dateValue === 'object' && typeof dateValue.toDate === 'function',
      stringified: JSON.stringify(dateValue),
      converted: toSafeDate(dateValue)
    });
  }
}